/**
 * Core Poker Game Types (Texas Hold'em)
 *
 * This file defines all TypeScript types, interfaces, enums, and constants
 * for the Poker game implementation. It follows the design specified in
 * .kiro/specs/poker-game/design.md
 *
 * Requirements: 1.1, 2.1, 8.2, 12.1, 17.1
 */

// ============================================================================
// Card Types
// ============================================================================

/** Card suits */
export type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';

/** Card ranks: 2 through Ace */
export type Rank = '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';

/** A single playing card with suit and rank */
export interface Card {
  suit: Suit;
  rank: Rank;
}

/**
 * Numeric rank values for comparison.
 * 2=2, ..., 10=10, J=11, Q=12, K=13, A=14
 * Ace can also be 1 for low straights (A-2-3-4-5)
 */
export const RANK_VALUES: Record<Rank, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
  '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14,
};

/** All four suits */
export const SUITS: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];

/** All thirteen ranks in ascending order */
export const RANKS: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

// ============================================================================
// Deck Types
// ============================================================================

/**
 * Deck state tracking all cards.
 * Full deck: 4 suits × 13 ranks = 52 cards.
 * Per hand: 2 × numPlayers (hole) + 3 (burn) + 5 (community) = 2n + 8 cards used
 */
export interface Deck {
  cards: Card[];        // Remaining cards in the deck
  dealtCards: Card[];   // Cards already dealt (hole cards + community)
  burnCards: Card[];    // Burn cards (tracked separately for verification)
}

// ============================================================================
// Hand Evaluation Types
// ============================================================================

/** Hand rankings from lowest (HIGH_CARD = 0) to highest (ROYAL_FLUSH = 9) */
export enum HandRank {
  HIGH_CARD = 0,
  ONE_PAIR = 1,
  TWO_PAIR = 2,
  THREE_OF_A_KIND = 3,
  STRAIGHT = 4,
  FLUSH = 5,
  FULL_HOUSE = 6,
  FOUR_OF_A_KIND = 7,
  STRAIGHT_FLUSH = 8,
  ROYAL_FLUSH = 9,
}

/**
 * Result of evaluating a poker hand.
 * comparisonKey format: [handRank, primaryRankValue, secondaryRankValue, ...kickers]
 * Examples:
 *   Full House K-K-K-10-10: [6, 13, 10]
 *   Two Pair A-A-7-7-K:     [2, 14, 7, 13]
 *   High Card A-K-Q-J-9:    [0, 14, 13, 12, 11, 9]
 */
export interface HandResult {
  rank: HandRank;
  rankName: string;              // e.g., "Full House, Kings over Tens"
  bestCards: Card[];             // The 5 cards forming the best hand
  comparisonKey: number[];       // Numeric tuple for deterministic comparison
}

// ============================================================================
// Player Types
// ============================================================================

/** Player status during a hand or game */
export type PlayerStatus = 'active' | 'folded' | 'all-in' | 'eliminated' | 'sitting-out';

/** AI difficulty levels */
export type PokerDifficulty = 'easy' | 'medium' | 'hard';

/** Complete player state (server-side / engine-side — includes hole cards) */
export interface PlayerState {
  id: string;
  name: string;
  seatIndex: number;            // 0-7
  chipStack: number;
  holeCards: Card[];             // Always 2 cards (empty if folded/eliminated)
  status: PlayerStatus;
  currentBet: number;           // Amount bet in current betting round
  totalBetThisHand: number;     // Total amount bet across all rounds this hand
  isDealer: boolean;
  isSmallBlind: boolean;
  isBigBlind: boolean;
  isHuman: boolean;
  aiDifficulty?: PokerDifficulty;
  hasActedThisRound: boolean;
  showCards: boolean;            // Whether hole cards are visible (showdown)
}

/** Visible player state (client-side — hides opponents' hole cards) */
export interface VisiblePlayerState {
  id: string;
  name: string;
  seatIndex: number;
  chipStack: number;
  holeCards: Card[];             // Empty for opponents unless showCards is true
  status: PlayerStatus;
  currentBet: number;
  isDealer: boolean;
  isSmallBlind: boolean;
  isBigBlind: boolean;
  hasActedThisRound: boolean;
  showCards: boolean;
}

// ============================================================================
// Betting Action Types
// ============================================================================

/** Available betting action types */
export type ActionType = 'fold' | 'check' | 'call' | 'raise' | 'all-in';

/** A player's betting action */
export interface PlayerAction {
  type: ActionType;
  amount?: number;              // Required for 'raise', optional for 'all-in' (uses full stack)
}

/** A valid action available to the current player */
export interface ValidAction {
  type: ActionType;
  minAmount?: number;           // For raise: minimum raise amount
  maxAmount?: number;           // For raise: maximum raise amount (player's stack)
  callAmount?: number;          // For call: amount needed to call
}

/** Result of submitting a betting action */
export interface ActionResult {
  success: boolean;
  action?: ActionRecord;
  error?: string;
  handComplete?: boolean;       // Did this action end the hand?
  roundComplete?: boolean;      // Did this action end the betting round?
  showdown?: ShowdownResult;    // Showdown results if hand ended at showdown
}

/** Record of a completed action for history tracking */
export interface ActionRecord {
  playerId: string;
  playerName: string;
  action: ActionType;
  amount: number;
  phase: HandPhase;
  timestamp: number;
}

// ============================================================================
// Pot Management Types
// ============================================================================

/**
 * Information about a pot (main or side).
 * Side pot example: Player A all-in for 100, players B & C bet 200 each:
 *   Main pot: 100 × 3 = 300 (A, B, C eligible)
 *   Side pot: 100 × 2 = 200 (B, C eligible)
 */
export interface PotInfo {
  amount: number;
  eligiblePlayerIds: string[];  // Players who can win this pot
  isMainPot: boolean;
}

// ============================================================================
// Hand Phase and Betting Round Types
// ============================================================================

/** Phases of a single hand */
export type HandPhase = 'pre-deal' | 'pre-flop' | 'flop' | 'turn' | 'river' | 'showdown' | 'complete';

/** Betting round identifiers */
export type BettingRound = 'pre-flop' | 'flop' | 'turn' | 'river';

/** Blind level configuration */
export interface BlindLevel {
  smallBlind: number;
  bigBlind: number;
}

// ============================================================================
// Community Card Types
// ============================================================================

/** History of community cards dealt at each stage */
export interface CommunityCardHistory {
  flop: Card[];                 // 3 cards
  turn: Card | null;            // 1 card
  river: Card | null;           // 1 card
}

// ============================================================================
// Showdown Types
// ============================================================================

/** Complete showdown result */
export interface ShowdownResult {
  playerResults: ShowdownPlayerResult[];
  potDistributions: PotDistribution[];
}

/** Individual player's showdown result */
export interface ShowdownPlayerResult {
  playerId: string;
  playerName: string;
  holeCards: Card[];
  bestHand: HandResult;
}

/** Distribution of a single pot at showdown */
export interface PotDistribution {
  potIndex: number;
  potAmount: number;
  winnerIds: string[];          // Multiple if split pot
  amountPerWinner: number;
  winningHand: HandResult;
  oddChipRecipient?: string;    // Player closest left of dealer gets odd chip
}

// ============================================================================
// Game State Types
// ============================================================================

/** Complete poker game state for persistence, synchronization, and serialization */
export interface PokerGameState {
  // Table configuration
  tableSize: number;            // 2-8
  buyIn: number;
  blindLevel: BlindLevel;

  // Players
  players: PlayerState[];
  dealerSeatIndex: number;
  smallBlindSeatIndex: number;
  bigBlindSeatIndex: number;

  // Current hand
  handNumber: number;
  handPhase: HandPhase;
  deck: Deck;
  communityCards: Card[];
  pots: PotInfo[];
  currentBettingRound: BettingRound | null;
  currentPlayerIndex: number;   // Index in players array
  lastRaiseAmount: number;      // For minimum raise enforcement
  actionHistory: ActionRecord[];

  // Game-level state
  gameStatus: PokerGameStatus;
  handHistory: HandSummary[];   // Summary of completed hands
}

/** Game status discriminated union */
export type PokerGameStatus =
  | { type: 'waiting' }         // Waiting for players (multiplayer)
  | { type: 'active' }          // Game in progress
  | { type: 'handInProgress' }  // Hand currently being played
  | { type: 'betweenHands' }    // Between hands
  | { type: 'complete'; winnerId: string }; // Game over

/** Visible game state sent to a specific player — hides opponents' hole cards */
export interface VisibleGameState {
  tableSize: number;
  buyIn: number;
  blindLevel: BlindLevel;
  players: VisiblePlayerState[];
  dealerSeatIndex: number;
  smallBlindSeatIndex: number;
  bigBlindSeatIndex: number;
  handNumber: number;
  handPhase: HandPhase;
  communityCards: Card[];
  pots: PotInfo[];
  currentBettingRound: BettingRound | null;
  currentPlayerIndex: number;
  actionHistory: ActionRecord[];
  gameStatus: PokerGameStatus;
}

/** Summary of a completed hand */
export interface HandSummary {
  handNumber: number;
  winnerId: string;
  winnerName: string;
  winningHand?: HandResult;     // null if won by fold
  potAmount: number;
  playerCount: number;
  phase: HandPhase;             // Phase when hand ended
}

// ============================================================================
// Game Configuration Types
// ============================================================================

/** Configuration for initializing a poker game */
export interface PokerGameConfig {
  tableSize: number;            // 2-8
  buyIn: number;                // Default 1000
  blindLevel: BlindLevel;       // Default { smallBlind: 10, bigBlind: 20 }
  players: PlayerConfig[];
}

/** Configuration for a single player */
export interface PlayerConfig {
  id: string;
  name: string;
  isHuman: boolean;
  aiDifficulty?: PokerDifficulty;
}

// ============================================================================
// Game Result and Statistics Types
// ============================================================================

/** Result of a completed poker game for statistics tracking */
export interface PokerGameResult {
  gameId: string;
  mode: 'singleplayer' | 'multiplayer-realtime';
  difficulty?: PokerDifficulty;
  finalPosition: number;        // 1 = winner, 2 = second, etc.
  handsPlayed: number;
  handsWon: number;
  totalChipsWon: number;
  totalChipsLost: number;
  biggestPotWon: number;
  duration: number;             // seconds
  completedAt: number;
}

/** Complete poker statistics across all modes and difficulties */
export interface PokerStatistics {
  userId?: string;
  singlePlayer: {
    easy: PokerGameStats;
    medium: PokerGameStats;
    hard: PokerGameStats;
  };
  onlineMultiplayer: PokerGameStats;
  totalGames: number;
  totalPlayTime: number;        // seconds
}

/** Statistics for a specific poker game category */
export interface PokerGameStats {
  gamesPlayed: number;
  gamesWon: number;
  winPercentage: number;
  handsPlayed: number;
  handsWon: number;
  totalChipsWon: number;
  totalChipsLost: number;
  biggestPotWon: number;
}

// ============================================================================
// Game Settings Types
// ============================================================================

/** Game settings for mode selection. No local-multiplayer — hole cards must stay hidden. */
export interface PokerGameSettings {
  mode: 'singleplayer' | 'multiplayer-realtime';
  aiDifficulty?: PokerDifficulty;
  aiOpponentCount?: number;     // 1-7
  tableSize?: number;           // 2-8 for multiplayer
  blindLevel?: BlindLevel;
  buyIn?: number;
  isPrivate?: boolean;
}

// ============================================================================
// Component Props Types
// ============================================================================

/** PokerTable component props */
export interface PokerTableProps {
  engine: any;                  // PokerEngine (avoid circular import)
  gameState: VisibleGameState;
  currentPlayerId: string;
  onAction: (action: PlayerAction) => void;
  isMultiplayer: boolean;
  turnTimeRemaining?: number;
}

/** BettingControls component props */
export interface BettingControlsProps {
  validActions: ValidAction[];
  pot: number;
  callAmount: number;
  minRaise: number;
  maxRaise: number;
  playerStack: number;
  onAction: (action: PlayerAction) => void;
  disabled: boolean;
}

/** CardDisplay component props */
export interface CardDisplayProps {
  card: Card | null;            // null = face-down
  size?: 'small' | 'medium' | 'large';
  faceDown?: boolean;
  animateReveal?: boolean;
  highlighted?: boolean;        // Part of winning hand
}

/** HandHistory component props */
export interface HandHistoryProps {
  actions: ActionRecord[];
  communityCards: CommunityCardHistory;
  currentPhase: HandPhase;
}

/** PokerGameModeSelector component props */
export interface PokerGameModeSelectorProps {
  onStartGame: (settings: PokerGameSettings) => void;
  onJoinPrivateGame: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

/** PokerGame main container component props */
export interface PokerGameProps {
  onExit?: () => void;
  initialRoomCode?: string;     // From deep link
}

// ============================================================================
// Constants
// ============================================================================

/** Default blind level: small blind 10, big blind 20 */
export const DEFAULT_BLIND_LEVEL: BlindLevel = { smallBlind: 10, bigBlind: 20 };

/** Default buy-in amount: 1000 chips */
export const DEFAULT_BUY_IN = 1000;
